/**
 * Dashboard+ — Smart Assist chip strip (Phase 16.2).
 *
 * Renders the `assist` array from /api/dashboard/plus. Each chip is a
 * one-click action with a deep-link href. Phase 16.2 additions:
 *   - dismissible/snoozable for 24h via X icon
 *   - icon-prefixed alert variant (when chip.tone='rose' or 'amber')
 *   - replaces the old PriorityAlerts card row (now removed — DEDUP)
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Lightbulb, X, AlertTriangle, Receipt, Plane, UserMinus, FileWarning } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TONE = {
  rose:    'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100',
  amber:   'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100',
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100',
  slate:   'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100',
};

// Per-chip icon picker. Falls back to Lightbulb. Keeps the chip glanceable
// without re-encoding the label text.
const CHIP_ICON = {
  'vouchers-missing': Receipt,
  'overdue-invoices': AlertTriangle,
  'departures-48h':   Plane,
  'cold-leads':       UserMinus,
  'docs-expiring':    FileWarning,
};

const SmartAssist = ({ chips = [], onChange }) => {
  const [dismissing, setDismissing] = useState(null);
  if (!chips || chips.length === 0) return null;

  const dismiss = async (e, chipId) => {
    e.preventDefault();
    e.stopPropagation();
    setDismissing(chipId);
    try {
      await axios.post(
        `${BACKEND_URL}/api/dashboard/smart-assist/dismiss`,
        { chipId, snoozeHours: 24 },
        { withCredentials: true },
      );
      toast.success('Snoozed for 24 hours');
      onChange?.();  // Parent re-fetches /plus so the chip disappears.
    } catch (err) {
      toast.error('Could not snooze chip');
    } finally {
      setDismissing(null);
    }
  };

  return (
    <div
      className="flex items-center gap-1.5 overflow-x-auto"
      style={{ scrollbarWidth: 'none' }}
      data-testid="smart-assist"
    >
      <Lightbulb className="h-3.5 w-3.5 text-amber-500 shrink-0" />
      <span className="text-[11px] font-semibold text-slate-700 mr-1 shrink-0">Smart assist</span>
      {chips.map((c) => (
        <AssistChip
          key={c.id}
          c={c}
          onDismiss={(e) => dismiss(e, c.id)}
          dismissing={dismissing === c.id}
        />
      ))}
    </div>
  );
};

const AssistChip = ({ c, onDismiss, dismissing }) => {
  const Icon = CHIP_ICON[c.id] || Lightbulb;
  return (
    <span
      className={`group inline-flex items-center gap-1 text-[11px] pl-2 pr-1 py-0.5 rounded-full border transition-colors shrink-0 whitespace-nowrap ${TONE[c.tone] || TONE.slate}`}
      data-testid={`assist-${c.id}-wrap`}
    >
      <Link
        to={c.href || '#'}
        data-testid={`assist-${c.id}`}
        className="inline-flex items-center gap-1 hover:no-underline"
      >
        <Icon className="h-3 w-3" />
        <span className="font-medium">{c.label}</span>
      </Link>
      <button
        type="button"
        onClick={onDismiss}
        disabled={dismissing}
        aria-label={`Snooze ${c.label} for 24 hours`}
        data-testid={`assist-${c.id}-dismiss`}
        className="ml-1 opacity-50 hover:opacity-100 transition-opacity rounded-full p-0.5 hover:bg-white/60"
      >
        <X className="h-2.5 w-2.5" />
      </button>
    </span>
  );
};

export default SmartAssist;
